'use client';

import {
  BottomSheetHandleArea,
  BottomSheetTitle,
  Dim,
  IcBoldClose,
  IconButton,
  Sheet,
  SheetContent,
  SheetDimMotion,
  SheetMotion,
  SheetPortal,
  Typography,
} from '@musinsa/mds';
import { twMerge } from 'tailwind-merge';
import { usePreventBodyScroll } from '../../../../common/hooks/usePreventBodyScroll';

export interface ContentGuideInfoSheetSection {
  title: string;
  items: string[];
}

export interface ContentGuideInfoSheetProps {
  open: boolean;
  onClose: () => void;
  /** 시트 헤더 타이틀 (예: "콘텐츠 제작 가이드") */
  sheetTitle: string;
  /** 섹션 N개 (제목 + 점 목록) */
  sections: ContentGuideInfoSheetSection[];
  className?: string;
}

/**
 * 콘텐츠 가이드 안내 바텀시트 — "제목 + 점 목록" 섹션이 반복되는 구조.
 *
 * 간격 규칙(.claude/rules/figma-list-spacing.md 적용):
 * - 섹션 사이 16px = contentArea `gap-y-16`
 * - 섹션 안(제목↔항목, 항목↔항목) 8px = 섹션/목록 `gap-y-8`
 * - 리스트 가장자리(상 4 / 하 12 / 좌우 16) = contentArea padding
 * 항목은 위치를 모르고(첫/끝 분기 없음), 가장자리·간격은 전부 컨테이너 책임.
 */
export const ContentGuideInfoSheet = ({
  open,
  onClose,
  sheetTitle,
  sections,
  className,
}: ContentGuideInfoSheetProps) => {
  // 마운트 위치(상세/진행/신청완료 등 6곳)와 무관하게 시트가 열리면 배경 스크롤 잠금.
  // SeedingTermsSheet 선례와 동일 — 락을 시트 컴포넌트가 책임져 마운트별 누락을 없앤다. (NOTICKET scroll-lock)
  usePreventBodyScroll({ open });

  return (
    <Sheet direction="bottom" open={open} onOpenChange={(next) => !next && onClose()}>
      <SheetPortal>
        <SheetContent onPointerDownOutside={onClose}>
          <SheetMotion direction="bottom">
            <div
              className={twMerge(
                'flex flex-col bg-white rounded-t-[8px] safe-area-bottom',
                className,
              )}
            >
              <BottomSheetHandleArea />
              <BottomSheetTitle>
                <div className="flex items-center justify-between px-[16px]! py-[9px]!">
                  <Typography variant="textEtc16Med" colorScheme="black">
                    {sheetTitle}
                  </Typography>
                  {/* 닫기 아이콘 — Figma Icon/28/Close(★UIIconBold★) = bold 28px. (SID-4430 수정1) */}
                  <IconButton size="size28x28" onClick={onClose} aria-label="닫기">
                    <IcBoldClose />
                  </IconButton>
                </div>
              </BottomSheetTitle>

              {/* contentArea — 섹션 사이 16, 가장자리 상4/하12/좌우16 */}
              <div className="flex flex-col gap-y-[16px] px-[16px]! pt-[4px]! pb-[12px]!">
                {sections.map((section) => (
                  // 섹션 — 제목↔항목, 항목↔항목 8
                  <div key={section.title} className="flex flex-col gap-y-[8px]">
                    <Typography variant="body14pxMed" colorScheme="black">
                      {section.title}
                    </Typography>
                    <ul className="flex flex-col gap-y-[8px]">
                      {section.items.map((item) => (
                        <li key={item} className="flex items-start gap-[4px]">
                          <Typography
                            variant="body13pxReg"
                            colorScheme="gray-600"
                            className="shrink-0"
                          >
                            •
                          </Typography>
                          <Typography
                            variant="body13pxReg"
                            colorScheme="gray-600"
                            className="flex-1"
                          >
                            {item}
                          </Typography>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </SheetMotion>
        </SheetContent>
        <SheetDimMotion>
          <Dim />
        </SheetDimMotion>
      </SheetPortal>
    </Sheet>
  );
};
